/**
 * 面接モードで学生が選べる面接官の一覧（#1863）。
 *
 * バックエンドの `Interviewer`（backend/app/models/interviewer.rb）と対になる。識別子の集合は
 * 両側のテストで固定している。片方だけ増やすと、画面が送った値をサーバが拒否する。
 *
 * サーバから受け取った識別子は必ず resolveInterviewer で一覧を引き、一覧に定義された
 * ディレクトリだけを使う。識別子の文字列をパスへ直接連結しない。
 */
export type InterviewerKey = "female" | "male";

export interface Interviewer {
  key: InterviewerKey;
  /** 開始画面の選択カードに出す名称 */
  label: string;
  /** 映像クリップ（idle / speaking / nodding / thinking / bow と質問別映像）の置き場所 */
  videoDir: string;
  /** 共通質問の事前生成音声の置き場所 */
  audioDir: string;
}

// 現行の女性面接官は素材を従来の場所に据え置く。配信パスとモバイルの同梱パスに触れないため。
export const INTERVIEWERS: readonly Interviewer[] = [
  {
    key: "female",
    label: "女性面接官",
    videoDir: "/videos/interviewer",
    audioDir: "/audio/questions",
  },
  {
    key: "male",
    label: "男性面接官",
    videoDir: "/videos/interviewer/male",
    audioDir: "/audio/questions/male",
  },
  // CG 面接官（3D 映画風の CG キャラクター）は素材が揃ってから後続 PR で足す。
  // 識別子 cg、映像 /videos/interviewer/cg、音声 /audio/questions/cg を予定している。
];

export const DEFAULT_INTERVIEWER: Interviewer = INTERVIEWERS[0];
export const DEFAULT_INTERVIEWER_KEY: InterviewerKey = DEFAULT_INTERVIEWER.key;

/**
 * 識別子から面接官を返す。未知の値・未指定は既定の女性面接官に丸める。
 * 古い応答（識別子を持たない）や保存済みのおかしな値でも、面接が始められない状態にはしない。
 */
export function resolveInterviewer(key: unknown): Interviewer {
  return INTERVIEWERS.find((i) => i.key === key) ?? DEFAULT_INTERVIEWER;
}

/**
 * 面接官の選択を画面に出すか（#1863）。
 * 男性面接官の映像素材と声の設定が揃った環境でだけ有効にする。無効な環境では選択の UI を出さず、
 * これまでどおり女性面接官で面接を始める。サーバ側でも声が未設定の面接官は拒否する。
 */
export function isInterviewerSelectionEnabled(): boolean {
  return process.env.NEXT_PUBLIC_FEATURE_INTERVIEWER_SELECTION === "true";
}

/** 選択カードのサムネイル（待機映像の先頭フレーム） */
export function interviewerThumbnailSrc(interviewer: Interviewer): string {
  return `${interviewer.videoDir}/thumb.jpg`;
}

/** 待機映像のパス。開始画面のプレビューに使う */
export function interviewerIdleClipSrc(interviewer: Interviewer): string {
  return `${interviewer.videoDir}/idle.mp4`;
}
